/**
 * Screenshot clocks become a UTC HH:MM[:SS] string.
 * A bare clock has no zone, so its digits are already the UTC clock the API
 * sent. An offset, Z, or zone abbreviation is shifted to UTC.
 */

const ZONE_OFFSET_MINUTES = {
  UTC: 0,
  GMT: 0,
  BST: 60,
  CET: 60,
  CEST: 120,
  WET: 0,
  WEST: 60,
};

function pad(part) {
  return String(part).padStart(2, "0");
}

function validClock(hour, minute, second) {
  return hour <= 23 && minute <= 59 && second <= 59;
}

function formatClock(hour, minute, second, withSeconds) {
  if (!validClock(hour, minute, second)) return null;
  if (!withSeconds) return `${pad(hour)}:${pad(minute)}`;
  return `${pad(hour)}:${pad(minute)}:${pad(second)}`;
}

function shiftClockToUtc(hour, minute, second, offsetMinutes) {
  if (!validClock(hour, minute, second)) return null;
  let total = hour * 3600 + minute * 60 + second - offsetMinutes * 60;
  total = ((total % 86400) + 86400) % 86400;
  return formatClock(
    Math.floor(total / 3600),
    Math.floor((total % 3600) / 60),
    total % 60,
    true
  );
}

function offsetMinutes(sign, hours, minutes) {
  const magnitude = Number(hours) * 60 + Number(minutes);
  return sign === "-" ? -magnitude : magnitude;
}

/**
 * UTC clock, or null when the text is empty or not a time.
 * Bare HH:MM stays HH:MM. Anything with seconds, a fraction, a zone, or a
 * date is HH:MM:SS in UTC.
 */
export function normalizeActionedAtTimeToUtc(value) {
  if (value == null) return null;
  const text = String(value).trim();
  if (!text) return null;

  const bare = /^(\d{1,2}):(\d{2})(?::(\d{2}))?(?:\.\d+)?$/.exec(text);
  if (bare) {
    return formatClock(
      Number(bare[1]),
      Number(bare[2]),
      bare[3] == null ? 0 : Number(bare[3]),
      bare[3] != null || /\.\d+$/.test(text)
    );
  }

  const ampm = /^(\d{1,2}):(\d{2})(?::(\d{2}))?(?:\.\d+)?\s*([AaPp][Mm])$/.exec(
    text
  );
  if (ampm) {
    let hour = Number(ampm[1]);
    const minute = Number(ampm[2]);
    const second = ampm[3] == null ? 0 : Number(ampm[3]);
    if (hour > 12) return null;
    if (ampm[4].toLowerCase() === "am") {
      if (hour === 12) hour = 0;
    } else if (hour !== 12) {
      hour += 12;
    }
    return formatClock(hour, minute, second, ampm[3] != null || /\.\d+/.test(text));
  }

  const clockOffset =
    /^(\d{1,2}):(\d{2})(?::(\d{2}))?(?:\.\d+)?\s*([+-])(\d{2}):?(\d{2})$/.exec(
      text
    );
  if (clockOffset) {
    return shiftClockToUtc(
      Number(clockOffset[1]),
      Number(clockOffset[2]),
      clockOffset[3] == null ? 0 : Number(clockOffset[3]),
      offsetMinutes(clockOffset[4], clockOffset[5], clockOffset[6])
    );
  }

  const clockZone =
    /^(\d{1,2}):(\d{2})(?::(\d{2}))?(?:\.\d+)?\s+([A-Za-z]{2,5})$/.exec(text);
  if (clockZone) {
    const zoneOffset = ZONE_OFFSET_MINUTES[clockZone[4].toUpperCase()];
    if (zoneOffset == null) return null;
    return shiftClockToUtc(
      Number(clockZone[1]),
      Number(clockZone[2]),
      clockZone[3] == null ? 0 : Number(clockZone[3]),
      zoneOffset
    );
  }

  const dated =
    /^(\d{4}-\d{2}-\d{2})[T ](\d{1,2}):(\d{2})(?::(\d{2}))?(?:\.\d+)?(?:\s*([Zz]|[+-]\d{2}:?\d{2}))?$/.exec(
      text
    );
  if (!dated) return null;
  const hour = Number(dated[2]);
  const minute = Number(dated[3]);
  const second = dated[4] == null ? 0 : Number(dated[4]);
  const zone = dated[5];
  if (!zone) return formatClock(hour, minute, second, true);
  if (zone.toUpperCase() === "Z") {
    return shiftClockToUtc(hour, minute, second, 0);
  }
  const zoneMatch = /^([+-])(\d{2}):?(\d{2})$/.exec(zone);
  if (!zoneMatch) return null;
  return shiftClockToUtc(
    hour,
    minute,
    second,
    offsetMinutes(zoneMatch[1], zoneMatch[2], zoneMatch[3])
  );
}
